<template>
  <div v-click-outside="()=>{ sidebarOpen = false }" :class="{ open: sidebarOpen }" class="sidebar bg-primary" role="tablist">
    <div class="sidebar-logo mx-4 mb-3 py-3 text-center border-bottom border-secondary position-relative">
      <router-link to="/"><img v-img-fallback="{ loading: imgBaseUrl + 'pixel.png', error: imgBaseUrl + 'kerlink-light.png' }" :src="imgBaseUrl + 'logo/logo-light.png'" alt="Logo" class="img-fluid"></router-link>
      <div class="text-secondary">Wanesy Management Center</div>
      <div class="text-secondary version">{{ cleanVersion }}</div>
    </div>

    <!-- Mobile only -->
    <ul class="nav-top-icons d-flex d-md-none justify-content-around align-items-center">
      <router-link :to="`/profile/${$store.getters.authenticatedUserCustomer.id}/${$store.getters.authenticatedUser.id}`" tag="li">
        <a id="sidebarProfileLink" :title="$t('sidebar:icons.profile') | capitalize">
          <avatar :name="$store.getters.authenticatedUser.login"
                  :picture-url="$store.getters.authenticatedUser.avatar"
                  size="sm"
          />
        </a>
      </router-link>
      <li><a id="sidebarDocsLink" :title="$t('sidebar:icons.documentations') | capitalize" :href="$store.getters.constants.docsUrl" target="_blank"><i class="icon-file" /></a></li>
      <router-link tag="li" to="/about"><a id="sidebarAboutLink" :title="$t('sidebar:icons.about') | capitalize"><i class="icon-about" /></a></router-link>
      <li><a id="sidebarLogoutLink" :title="$t('sidebar:icons.signOut') | capitalize" @click="logout"><i class="icon-logout" /></a></li>
    </ul>

    <ul class="nav flex-column">
      <li :class="[managementMenuOpen ? null : 'collapsed', managementMenuActive ? 'active' : null]"
          :aria-expanded="managementMenuOpen ? 'true' : 'false'"
          aria-controls="managementMenu"
          @click="managementMenuOpen = !managementMenuOpen"
      >
        <i class="icon-monitoring" />
        <span>{{ $t('sidebar:management.category') | capitalize }}</span>
        <i class="icon-angle-right" />
      </li>
      <b-collapse id="managementMenu" v-model="managementMenuOpen">
        <router-link id="sidebarFleetsLink" to="/fleets">{{ $t('sidebar:management.fleets') | capitalize }}</router-link>
        <router-link id="sidebarGatewaysLink" to="/gateways">{{ $t('sidebar:management.gateways') | capitalize }}</router-link>
        <!-- Mobile only -->
        <router-link id="sidebarGatewaysSelectionLink" to="/selection/gateways" class="d-block d-md-none">
          {{ $t('sidebar:management.gatewaysSelection') | capitalize }} ({{ $store.getters.gatewaysSelection.length }} {{ $t('sidebar:management.gatewaysSelectionContent', {count:$store.getters.gatewaysSelection.length}) }})
        </router-link>

        <router-link id="sidebarClustersLink" to="/clusters">{{ $t('sidebar:management.clusters') | capitalize }}</router-link>
        <router-link id="sidebarMulticastGroupsLink" to="/multicast-groups">{{ $t('sidebar:management.multicastGroups') | capitalize }}</router-link>
        <router-link id="sidebarEndDevicesLink" to="/end-devices">{{ $t('sidebar:management.endDevices') | capitalize }}</router-link>
        <!-- Mobile only -->
        <router-link id="sidebarEndDevicesSelectionLink" to="/selection/end-devices" class="d-block d-md-none">
          {{ $t('sidebar:management.endDevicesSelection') | capitalize }} ({{ $store.getters.endDevicesSelection.length }} {{ $t('sidebar:management.endDevicesSelectionContent', {count:$store.getters.endDevicesSelection.length}) }})
        </router-link>

        <router-link id="sidebarKpiLink" to="/kpi">{{ $t('sidebar:management.kpi') | capitalize }}</router-link>
      </b-collapse>

      <!-- Mobile only -->
      <li :class="[alarmsMenuOpen ? null : 'collapsed', alarmsMenuActive ? 'active' : null]"
          :aria-expanded="alarmsMenuOpen ? 'true' : 'false'"
          aria-controls="alarmsMenu"
          class="d-flex d-md-none"
          @click="alarmsMenuOpen = !alarmsMenuOpen"
      >
        <i class="icon-alarm" />
        <span>{{ $t('sidebar:alarms.category') | capitalize }}</span>
        <i class="icon-angle-right" />
      </li>
      <b-collapse id="alarmsMenu" v-model="alarmsMenuOpen" class="d-md-none">
        <router-link id="sidebarGatewaysAlarmsLink" to="/alarms/gateways" class="d-flex justify-content-between align-items-center">
          {{ $t('sidebar:alarms.gateways') | capitalize }}
          <alarm-badge :alarm-count="gatewaysAlarmCount" />
        </router-link>
        <router-link id="sidebarEndDevicesAlarmsLink" to="/alarms/end-devices" class="d-flex justify-content-between align-items-center">
          {{ $t('sidebar:alarms.endDevices') | capitalize }}
          <alarm-badge :alarm-count="endDevicesAlarmCount" />
        </router-link>
        <router-link id="sidebarNetworkAlarmsLink" to="/alarms/network" class="d-flex justify-content-between align-items-center">
          {{ $t('sidebar:alarms.network') | capitalize }}
          <alarm-badge :alarm-count="networkAlarmCount" />
        </router-link>
      </b-collapse>

      <li v-if="isRole.user"
          :class="[workflowMenuOpen ? null : 'collapsed', workflowMenuActive ? 'active' : null]"
          :aria-expanded="workflowMenuOpen ? 'true' : 'false'"
          aria-controls="workflowMenu"
          @click="workflowMenuOpen = !workflowMenuOpen"
      >
        <i class="icon-workflow" />
        <span>{{ $t('sidebar:workflows.category') | capitalize }}</span>
        <i class="icon-angle-right" />
      </li>
      <b-collapse v-if="isRole.user" id="workflowMenu" v-model="workflowMenuOpen">
        <router-link v-if="isRole.admin" id="sidebarWorkflowTemplatesLink" to="/workflows/templates">{{ $t('sidebar:workflows.templates') | capitalize }}</router-link>
        <router-link v-if="isRole.user" id="sidebarRunningWorkflowsLink" to="/workflows/running">{{ $t('sidebar:workflows.running') | capitalize }}</router-link>
        <router-link v-if="isRole.user" id="sidebarCompletedWorkflowsLink" to="/workflows/completed">{{ $t('sidebar:workflows.completed') | capitalize }}</router-link>
      </b-collapse>

      <li v-if="isRole.admin"
          :class="[adminMenuOpen ? null : 'collapsed', adminMenuActive ? 'active' : null]"
          :aria-expanded="adminMenuOpen ? 'true' : 'false'"
          aria-controls="adminMenu"
          @click="adminMenuOpen = !adminMenuOpen"
      >
        <i class="icon-administration" />
        <span>{{ $t('sidebar:administration.category') | capitalize }}</span>
        <i class="icon-angle-right" />
      </li>
      <b-collapse v-if="isRole.admin" id="adminMenu" v-model="adminMenuOpen">
        <router-link v-if="isRole.superAdmin" id="sidebarAdminCustomersLink" to="/administration/customers">{{ $t('sidebar:administration.customers') | capitalize }}</router-link>
        <router-link id="sidebarAdminUsersLink" to="/administration/users">{{ $t('sidebar:administration.users') | capitalize }}</router-link>
        <router-link id="sidebarAdminFleetsLink" to="/administration/fleets">{{ $t('sidebar:administration.fleets') | capitalize }}</router-link>
        <router-link id="sidebarAdminGatewaysLink" to="/administration/gateways">{{ $t('sidebar:administration.gateways') | capitalize }}</router-link>
        <router-link id="sidebarAdminClustersLink" to="/administration/clusters">{{ $t('sidebar:administration.clusters') | capitalize }}</router-link>
        <router-link id="sidebarAdminMulticastGroupsLink" to="/administration/multicast-groups">{{ $t('sidebar:administration.multicastGroups') | capitalize }}</router-link>
        <router-link id="sidebarAdminEndDevicesLink" to="/administration/end-devices">{{ $t('sidebar:administration.endDevices') | capitalize }}</router-link>
        <router-link v-if="isRole.superAdmin" id="sidebarAdminSoftwaresLink" to="/administration/softwares">{{ $t('sidebar:administration.softwares') | capitalize }}</router-link>
      </b-collapse>

      <!-- Mobile only -->
      <router-link v-if="isRole.superAdmin" id="sidebarSettingsLink" to="/settings" tag="li" class="d-md-none lonely-item">
        <i class="icon-settings" />
        <span>{{ $t('sidebar:settings') | capitalize }}</span>
      </router-link>
      <router-link v-if="isRole.superAdmin" id="sidebarActionsLink" to="/actions" tag="li" class="d-md-none lonely-item">
        <i class="icon-logs" />
        <span>{{ $t('sidebar:actions') | capitalize }}</span>
      </router-link>
    </ul>

    <div class="sidebar-bg" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import eventBus from '@/modules/eventBus'

import vClickOutside from 'v-click-outside'
import BCollapse from 'bootstrap-vue/es/components/collapse/collapse'

export default {
  components: { BCollapse },

  directives: {
    clickOutside: vClickOutside.directive
  },

  data () {
    return {
      // For mobile use
      sidebarOpen: false,

      managementMenuOpen: false,
      managementMenuActive: false,
      alarmsMenuOpen: false,
      alarmsMenuActive: false,
      workflowMenuOpen: false,
      workflowMenuActive: false,
      settingsMenuOpen: false,
      settingsMenuActive: false,
      adminMenuOpen: false,
      adminMenuActive: false
    }
  },

  computed: {
    // mix the getters into computed with object spread operator
    ...mapGetters(['gatewaysAlarmCount', 'endDevicesAlarmCount', 'networkAlarmCount']),

    cleanVersion () {
      const pre = this.$utils.prerelease(_VERSION)
      const version = this.$utils.major(_VERSION) + '.' + this.$utils.minor(_VERSION)
      return version + (pre ? ' ' + pre[0] : '')
    }
  },

  watch: {
    // Watch for router to update menu and activate/open categories
    // https://github.com/vuejs/vue-router/issues/112#issuecomment-262464429
    '$route' () {
      this.sidebarOpen = false
      this.expandMenuCategory()
    }
  },

  mounted () {
    this.expandMenuCategory()

    // Listen to toggle sidebar event for mobile use
    eventBus.$on('sidebar:toggle', this.toggleSidebar)
  },

  beforeDestroy () {
    // Destroy listener to toggle sidebar event for mobile use
    eventBus.$off('sidebar:toggle', this.toggleSidebar)
  },

  methods: {
    toggleSidebar () {
      this.sidebarOpen = !this.sidebarOpen
    },

    // Check if a menu category should be activated and expanded
    // Do not reinit xMenuOpen boolean to keep menu open
    expandMenuCategory () {
      this.managementMenuActive = false
      this.alarmsMenuActive = false
      this.workflowMenuActive = false
      this.settingsMenuActive = false
      this.adminMenuActive = false

      switch (this.$route.meta.menu) {
        case 'management':
          this.managementMenuOpen = true
          this.managementMenuActive = true
          break
        case 'alarms':
          this.alarmsMenuOpen = true
          this.alarmsMenuActive = true
          break
        case 'workflows':
          this.workflowMenuOpen = true
          this.workflowMenuActive = true
          break
        case 'settings':
          this.settingsMenuOpen = true
          this.settingsMenuActive = true
          break
        case 'administration':
          this.adminMenuOpen = true
          this.adminMenuActive = true
          break
      }
    },

    // Logout from app
    logout () {
      this.$api.logout()
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
  @import "~@/scss/variables";

  $border-right-width:  3px;

  .sidebar {
    position: fixed;
    top: $navbar-height;
    bottom: 0;
    left: -$sidebar-width;
    z-index: 1040;
    width: $sidebar-width;
    overflow-x: hidden;
    overflow-y: auto;
    border-top: 1px solid $secondary;
    transition: all 0.35s ease;

    @media (min-width: $grid-breakpoint-md) {
      top: 0;
      left: 0;
      border-top: none;

      .sidebar-logo {
        margin-top: -3px;

        .version {
          font-size: 75%;
        }
      }
    }

    &.open {
      left: 0;
    }

    *:not(.sidebar-bg) {
      z-index: 4;
    }

    ul.nav-top-icons {
      padding: 15px 15px 15px 20px;
      margin-bottom: 0;
      list-style: none;

      li {
        a {
          [class*=" icon-"],
          [class^="icon-"] {
            color: $gray-300;
          }
        }

        &.active {
          a {
            [class*=" icon-"],
            [class^="icon-"] {
              color: $secondary;
            }

            .avatar {
              border: 1px solid $secondary;
              border-radius: 50%;
            }
          }
        }
      }
    }

    ul.nav {
      & > li {
        display: flex;
        justify-content: space-between;
        padding: 15px 15px 15px 20px;
        overflow: hidden;
        color: $gray-300;
        text-overflow: ellipsis;
        white-space: nowrap;
        cursor: pointer;

        & > span {
          flex: 1;
          padding-left: 10px;
        }

        .icon-angle-right {
          display: inline-block;
          transition: transform 0.35s ease;
        }

        &.collapsed {
          .icon-angle-right {
            transform: rotate(0deg);
          }
        }

        &:not(.collapsed) {
          background-color: $primary-dark;

          &.lonely-item:not(.active) {
            background-color: transparent;
          }

          .icon-angle-right {
            transform: rotate(90deg);
          }
        }

        &:hover {
          background-color: $primary-darker;
        }

        &.active,
        &.active:hover {
          font-weight: bold;
          color: $primary;
          background-color: $secondary;
        }
      }

      & > div {
        background-color: $primary-dark;

        a {
          display: block;
          padding: 15px 15px 15px 48px;
          overflow: hidden;
          color: $gray-300;
          text-overflow: ellipsis;
          white-space: nowrap;
          border-left: $border-right-width solid $primary-dark;

          &:hover {
            background-color: $primary-darker;
            border-left: $border-right-width solid $primary-darker;
          }

          &.active {
            color: $secondary;
            border-left: $border-right-width solid $secondary;
          }
        }
      }
    }

    .sidebar-bg {
      position: absolute;
      top: 0;
      left: 0;
      z-index: 1;
      display: block;
      width: 100%;
      height: 100%;
      background-image: url('~@/img/sidebar-bg.jpg');
      background-position: center center;
      background-size: cover;

      &::after {
        position: absolute;
        z-index: 3;
        display: block;
        width: 100%;
        height: 100%;
        content: "";
        background: $primary;
        opacity: 0.93;
      }
    }
  }
</style>
